import * as utils from '@/utils';
const defaultRouteFuncOptions = {
    shouldRetainQueryParameters: true,
};
/**
 * Merges `originalLocation` with options-driven additions, such as the redirect query param.
 *
 * @param router The active Vue router instance.
 * @param options Optional overrides for route getter behaviour.
 * @param originalLocation The base location to enrich.
 * @returns The final `Location` object to pass to `<router-link>` or `router.push/replace`.
 */
function makeLocation(router, options, originalLocation) {
    const { shouldRetainQueryParameters } = { ...defaultRouteFuncOptions, ...options };
    if (!shouldRetainQueryParameters) {
        return originalLocation;
    }
    return {
        ...originalLocation,
        query: { ...originalLocation.query, redirect: router.getQuery('redirect').extract() },
    };
}
/**
 * Factory that creates a `RouteGetter` for a named route with no dynamic params.
 *
 * @param locationName The Vue router route name.
 * @returns A `RouteGetter` for that route.
 */
function makeRouteGetter(locationName) {
    return (router, opts) => makeLocation(router, opts, { name: locationName });
}
/** Vue route for the login start page. */
export const routeLoginStart = makeRouteGetter('login');
/**
 * Vue route for the OTP verification page.
 *
 * @param router The active Vue router instance.
 * @param reqId The request identifier.
 * @param email The email address used for the OTP login.
 * @param options Optional overrides for route getter behaviour.
 * @returns A `Location` for the OTP verification page.
 */
export const routeOTPVerification = (router, reqId, email, options) => makeLocation(router, options, { name: 'login-otp-verification', query: { reqId, email } });
/**
 * Vue route for the account selection page after OTP login.
 *
 * @param router The active Vue router instance.
 * @param session The OTP session identifier.
 * @param code The OTP code entered by the user.
 * @param options Optional overrides for route getter behaviour.
 * @returns A `Location` for the account selection page.
 */
export const routeAccountSelection = (router, session, code, options) => makeLocation(router, options, {
    name: 'login-select-account',
    params: { session },
    query: { code },
});
/** Vue route for the SSO message page. */
export const routeSSO = makeRouteGetter('login-sso-message');
/** Vue route for the tenant list. */
export const routeTenantList = makeRouteGetter('login-tenant-list');
/**
 * Vue route for the tenant-specific login page.
 *
 * @param router The active Vue router instance.
 * @param tenantId The ID of the tenant to log in to.
 * @param options Optional overrides for route getter behaviour.
 * @returns A `Location` for the tenant login page.
 */
export const routeTenantLogin = (router, tenantId, options) => makeLocation(router, options, {
    name: 'login',
    params: { tenantId },
});
/**
 * Vue route for the forgot-password page.
 *
 * @param router The active Vue router instance.
 * @param tenantId The ID of the tenant to reset the password for.
 * @param options Optional overrides for route getter behaviour.
 * @returns A `Location` for the forgot-password page.
 */
export const routeForgotPassword = (router, tenantId, options) => makeLocation(router, options, {
    name: 'forgot',
    params: { tenantId },
});
/** @returns A `Location` for the registration start page. */
export const routeRegisterStart = makeRouteGetter('register-start');
/**
 * Builds a `Location` for the register account-details page.
 *
 * @param router The Vue Router instance.
 * @param token The pending registration token from the API.
 * @param email The email address to pre-fill on the details page.
 * @param options Optional overrides for route getter behaviour.
 * @returns A `Location` for the account-details registration page.
 */
export const routeRegisterDetails = (router, token, email, options) => makeLocation(router, options, {
    name: 'register-account-details',
    params: { token },
    query: { email },
});
/**
 * Extracts and validates the `redirect` query parameter from the current route.
 *
 * Only same-origin URLs are returned to prevent open-redirect vulnerabilities.
 *
 * @param router The active Vue router instance.
 * @returns A `Maybe<string>` containing the sanitised redirect path, or `Nothing`.
 */
export const getRedirectFromQuery = (router) => router
    .getQuery('redirect')
    .filter(r => utils.urlSameOrigin(r))
    .map(r => utils.stripURLOrigin(r));
/**
 * Redirects the user after a successful login.
 *
 * Uses the `redirect` query parameter if present and same-origin; falls back to `home`.
 *
 * @param router The active Vue router instance.
 * @returns The router's replace action.
 */
export const redirectAfterLogin = async (router) => router.replace(getRedirectFromQuery(router).orDefault({ name: 'home' }));
